import { customFunctions } from "@/config/customFunction";
import DefaultInstance, { MasterInstance } from "@/instance/axios";
import { dataStyle, headerStyle } from "@/utils/excelDownLoad";
import { multiSheetExcelDownload } from "@/utils/multiSheetExcelDownload";
import {
  AbstractItemsGet200ResponseRowsInner,
  AuthSignupPost201Response,
  AuthSignupPost201ResponseEquipment,
  DefectsGet200ResponseRowsInner,
  DepartmentsGet200ResponseRowsInner,
  DowntimeReasonsGet200ResponseRowsInner,
  GradesGet200ResponseRowsInner,
  ItemCategoriesGet200ResponseRowsInner,
  ItemGradeViewGet200ResponseRowsInner,
  ItemsGet200ResponseRowsInnerRoutingsInner,
  LocationsGet200ResponseRowsInner,
  OperationsGet200ResponseRowsInner,
  PartnersGet200ResponseRowsInner,
  SitesGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

export const useStandardMultiSheetExcelDownload = (setLoading: (state: boolean) => void) => {
  const toString = (value: any) => {
    return value ? value.toString() : "";
  };

  const { t } = useTranslation();
  const today = dayjs(new Date()).format("YYYYMMDD");

  // 청크 크기 설정
  const CHUNK_SIZE = 5;

  // 청크 처리 함수
  const chunkArray = (array: string | any[], size: number) => {
    return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
      array.slice(index * size, index * size + size),
    );
  };

  const downloadExcel = async () => {
    try {
      setLoading(true);
      const apiRequests = {
        items: DefaultInstance.itemGradeViewFindPost({}),
        equipments: MasterInstance.equipmentsFindPost({ populate: ["works", "location"] }),
        defects: DefaultInstance.defectsFindPost({}),
        operations: DefaultInstance.operationsFindPost({ populate: ["location"] }),
        routings: DefaultInstance.routingsFindPost({ populate: ["item", "operation", "mold"] }),
        sites: MasterInstance.sitesFindPost({}),
        locations: MasterInstance.locationsFindPost({}),
        // routingOutsources: DefaultInstance.routingOutsourcesFindPost({ populate: ["location"] }),
        // molds: MasterInstance.moldsFindPost({}),
        // moldStocks: MasterInstance.moldStocksFindPost({}),
        abstractItems: DefaultInstance.abstractItemsFindPost({}),
        itemCategories: MasterInstance.itemCategoriesFindPost({}),
        downtimeReasons: DefaultInstance.downtimeReasonsFindPost({}),
        ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING && {
          grades: MasterInstance.gradesFindPost({}),
        }),
        ...(customFunctions.ADD_STANDARD_INFO_DEPART_CODE_MENU_SETTING && {
          departments: MasterInstance.departmentsFindPost({}),
        }),
        ...(customFunctions.ADD_STANDARD_INFO_PARTNER_MENU_SETTING && {
          partners: MasterInstance.partnersFindPost({}),
        }),
        ...(customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING && {
          bomSearch: DefaultInstance.itemGradeViewFindPost({}),
        }),
        users: MasterInstance.usersFindPost({}),
      };

      const keys = Object.keys(apiRequests);
      const promises = Object.values(apiRequests);

      // 청크 처리
      const apiChunks = chunkArray(promises, CHUNK_SIZE);
      const resultChunks: any[] = [];

      for (const chunk of apiChunks) {
        const responses = await Promise.allSettled(chunk);
        resultChunks.push(...responses);
        await new Promise((resolve) => setTimeout(resolve, 500)); // 서버 과부하 방지
      }

      // Key별 결과 매핑
      const results: { [key: string]: any } = keys.reduce(
        (acc, key, index) => {
          const response = resultChunks[index];
          acc[key] = response.status === "fulfilled" ? response.value : null;
          return acc;
        },
        {} as { [key: string]: any },
      );

      const {
        items,
        equipments,
        defects,
        operations,
        routings,
        sites,
        locations,
        // routingOutsources,
        // molds,
        // moldStocks,
        abstractItems,
        itemCategories,
        downtimeReasons,
        grades,
        departments,
        partners,
        bomSearch,
        users,
      } = results;

      const sheetData = [
        {
          // 경남케미컬은 브랜치에서 따로 작업.
          sheetName: "품목",
          headerRow: [
            { v: "품목군", t: "s", s: headerStyle },
            { v: "품목명", t: "s", s: headerStyle },
            { v: "Grade명", t: "s", s: headerStyle },
            { v: "Color", t: "s", s: headerStyle },
            { v: "형태", t: "s", s: headerStyle },
            { v: "안전재고", t: "s", s: headerStyle },
            { v: "단위", t: "s", s: headerStyle },
            { v: "로트 관리기준 (로트명)", t: "s", s: headerStyle },
            { v: "로트 관리기준 (유효기한)", t: "s", s: headerStyle },
          ],
          dataRows:
            items.data?.map((item: ItemGradeViewGet200ResponseRowsInner) => [
              { v: t(toString(item.itemType)), t: "s", s: dataStyle },
              { v: toString(item.name), t: "s", s: dataStyle },
              { v: toString(item.gradeName), t: "s", s: dataStyle },
              { v: toString(item.itemCategoryName), t: "s", s: dataStyle },
              { v: toString(item.abstractItemCode), t: "s", s: dataStyle },
              { v: toString(item.safetyStock), t: "s", s: dataStyle },
              { v: toString(item.unitText), t: "s", s: dataStyle },
              { v: toString(item.usesLotName), t: "s", s: dataStyle },
              { v: toString(item.usesLotExpiration), t: "s", s: dataStyle },
            ]) ?? [],
        },
        {
          sheetName: "설비",
          headerRow: [
            { v: "사업장코드", t: "s", s: headerStyle },
            { v: "설비코드", t: "s", s: headerStyle },
            { v: "설비명", t: "s", s: headerStyle },
            { v: "설비유형", t: "s", s: headerStyle },
            { v: "전압", t: "s", s: headerStyle },
            { v: "원부자재 로케이션", t: "s", s: headerStyle },
            { v: "입고 로케이션", t: "s", s: headerStyle },
            { v: "품목 생산 로케이션", t: "s", s: headerStyle },
            { v: "시퀀스(순서)", t: "s", s: headerStyle },
            { v: "비가동사유코드", t: "s", s: headerStyle },
          ],
          dataRows: equipments.data?.map((equipment: AuthSignupPost201ResponseEquipment) => [
            { v: toString(equipment.siteCode), t: "s", s: dataStyle },
            { v: toString(equipment.code), t: "s", s: dataStyle },
            { v: toString(equipment.name), t: "s", s: dataStyle },
            { v: toString(equipment.equipmentType), t: "s", s: dataStyle },
            { v: toString(equipment.volt), t: "s", s: dataStyle },
            { v: toString(equipment.fromLocationName), t: "s", s: dataStyle },
            { v: toString(equipment.toLocationName), t: "s", s: dataStyle },
            { v: toString(equipment.targetLocationName), t: "s", s: dataStyle },
            { v: toString(equipment.seq), t: "s", s: dataStyle },
            { v: toString(equipment.downtimeReasonCode), t: "s", s: dataStyle },
          ]),
        },
        {
          sheetName: "불량",
          headerRow: [
            { v: "불량코드", t: "s", s: headerStyle },
            { v: "불량명", t: "s", s: headerStyle },
            { v: "공정코드", t: "s", s: headerStyle },
          ],
          dataRows: defects.data?.map((defect: DefectsGet200ResponseRowsInner) => [
            { v: toString(defect.code), t: "s", s: dataStyle },
            { v: toString(defect.name), t: "s", s: dataStyle },
            { v: toString(defect.operationCode), t: "s", s: dataStyle },
          ]),
        },
        {
          sheetName: "공정",
          headerRow: [
            { v: "공정코드", t: "s", s: headerStyle },
            { v: "공정명", t: "s", s: headerStyle },
            { v: "사업장코드", t: "s", s: headerStyle },
            { v: "자재 출고 로케이션명", t: "s", s: headerStyle },
            { v: "재공품 입고 로케이션명", t: "s", s: headerStyle },
          ],
          dataRows: operations.data?.map((operation: OperationsGet200ResponseRowsInner) => [
            { v: toString(operation.code), t: "s", s: dataStyle },
            { v: toString(operation.name), t: "s", s: dataStyle },
            { v: toString(operation.siteCode), t: "s", s: dataStyle },
            { v: toString(operation.fromLocationName), t: "s", s: dataStyle },
            { v: toString(operation.toLocationName), t: "s", s: dataStyle },
          ]),
        },
        {
          sheetName: "공정도",
          headerRow: [
            { v: "품목군", t: "s", s: headerStyle },
            { v: "품목명", t: "s", s: headerStyle },
            { v: "Grade", t: "s", s: headerStyle },
            { v: "Color", t: "s", s: headerStyle },
            { v: "형태", t: "s", s: headerStyle },
            { v: "규격", t: "s", s: headerStyle },
            { v: "공정코드", t: "s", s: headerStyle },
            { v: "공정도유형", t: "s", s: headerStyle },
            { v: "금형코드", t: "s", s: headerStyle },
            { v: "소모시점", t: "s", s: headerStyle },
            { v: "공정도순서", t: "s", s: headerStyle },
          ],
          dataRows: routings.data?.map((routing: ItemsGet200ResponseRowsInnerRoutingsInner) => [
            { v: t(toString(routing.itemWithGrade?.itemType)), t: "s", s: dataStyle },
            { v: toString(routing.item?.name), t: "s", s: dataStyle },
            { v: toString(routing.itemWithGrade?.gradeName), t: "s", s: dataStyle },
            { v: toString(routing.itemWithGrade?.itemCategoryName), t: "s", s: dataStyle },
            { v: toString(routing.itemWithGrade?.abstractItemCode), t: "s", s: dataStyle },
            { v: toString(routing.item?.spec), t: "s", s: dataStyle },
            { v: toString(routing.operationCode), t: "s", s: dataStyle },
            { v: t(toString(routing.routingType)), t: "s", s: dataStyle },
            { v: toString(routing.moldCode), t: "s", s: dataStyle },
            { v: t(toString(routing.consumeType)), t: "s", s: dataStyle },
            { v: toString(routing.seq), t: "s", s: dataStyle },
          ]),
        },
        {
          sheetName: "사업장",
          headerRow: [
            { v: "사업장코드", t: "s", s: headerStyle },
            { v: "사업장명", t: "s", s: headerStyle },
          ],
          dataRows: sites.data?.map((site: SitesGet200ResponseRowsInner) => [
            { v: toString(site.code), t: "s", s: dataStyle },
            { v: toString(site.name), t: "s", s: dataStyle },
          ]),
        },
        {
          sheetName: "로케이션",
          headerRow: [
            { v: "로케이션명", t: "s", s: headerStyle },
            { v: "로케이션코드", t: "s", s: headerStyle },
            { v: "사업장코드", t: "s", s: headerStyle },
            ...(customFunctions.ADD_DEFECT_LOCATION
              ? [{ v: "가용 창고 여부", t: "s", s: headerStyle }]
              : []),
          ],
          dataRows: locations.data?.map((location: LocationsGet200ResponseRowsInner) => [
            { v: toString(location.name), t: "s", s: dataStyle },
            { v: toString(location.code), t: "s", s: dataStyle },
            { v: toString(location.siteCode), t: "s", s: dataStyle },
            ...(customFunctions.ADD_DEFECT_LOCATION
              ? [
                  {
                    v: location.isAvailable ? "가용" : "비가용",
                    t: "s",
                    s: dataStyle,
                  },
                ]
              : []),
          ]),
        },
        // {
        //   sheetName: "외주처",
        //   headerRow: [
        //     { v: t("outsource.name"), t: "s", s: headerStyle },
        //     { v: t("routing.code"), t: "s", s: headerStyle },
        //     { v: t("outsource.fromLocationName"), t: "s", s: headerStyle },
        //     { v: t("outsource.toLocationName"), t: "s", s: headerStyle },
        //   ],
        //   dataRows: routingOutsources.data?.map(
        //     (outsource: RoutingOutsourcesGet200ResponseRowsInner) => [
        //       { v: toString(outsource.name), t: "s", s: dataStyle },
        //       { v: toString(outsource.routingCode), t: "s", s: dataStyle },
        //       { v: toString(outsource.fromLocationName), t: "s", s: dataStyle },
        //       { v: toString(outsource.toLocationName), t: "s", s: dataStyle },
        //     ],
        //   ),
        // },
        // {
        //   sheetName: "금형",
        //   headerRow: [
        //     { v: t("mold.code"), t: "s", s: headerStyle },
        //     { v: t("site.code"), t: "s", s: headerStyle },
        //     { v: t("mold.capacity"), t: "s", s: headerStyle },
        //   ],
        //   dataRows: molds.data?.map((mold: MoldsGet200ResponseRowsInner) => [
        //     { v: toString(mold.code), t: "s", s: dataStyle },
        //     { v: toString(mold.siteCode), t: "s", s: dataStyle },
        //     { v: toString(mold.capacity), t: "s", s: dataStyle },
        //   ]),
        // },
        // {
        //   sheetName: "금형재고",
        //   headerRow: [
        //     { v: t("moldStock.code"), t: "s", s: headerStyle },
        //     { v: t("mold.code"), t: "s", s: headerStyle },
        //     { v: t("moldStock.limitCounter"), t: "s", s: headerStyle },
        //     { v: t("moldStock.inspectCounter"), t: "s", s: headerStyle },
        //     { v: t("moldStock.currentCounter"), t: "s", s: headerStyle },
        //   ],
        //   dataRows: moldStocks.data?.map((moldStock: MoldStocksGet200ResponseRowsInner) => [
        //     { v: toString(moldStock.code), t: "s", s: dataStyle },
        //     { v: toString(moldStock.moldCode), t: "s", s: dataStyle },
        //     { v: toString(moldStock.limitCounter), t: "s", s: dataStyle },
        //     { v: toString(moldStock.inspectCounter), t: "s", s: dataStyle },
        //     { v: toString(moldStock.currentCounter), t: "s", s: dataStyle },
        //   ]),
        // },
        {
          sheetName: "형태",
          headerRow: [{ v: "형태 코드", t: "s", s: headerStyle }],
          dataRows: abstractItems.data?.map(
            (abstractItem: AbstractItemsGet200ResponseRowsInner) => [
              { v: toString(abstractItem.code), t: "s", s: dataStyle },
            ],
          ),
        },
        {
          sheetName: "Color",
          headerRow: [{ v: "Color명", t: "s", s: headerStyle }],
          dataRows: itemCategories.data?.map((category: ItemCategoriesGet200ResponseRowsInner) => [
            { v: toString(category.name), t: "s", s: dataStyle },
          ]),
        },
        {
          sheetName: "비가동사유",
          headerRow: [
            { v: "순서", t: "s", s: headerStyle },
            { v: "비가동 사유 코드", t: "s", s: headerStyle },
            { v: "비가동 사유 명", t: "s", s: headerStyle },
            { v: "공정코드", t: "s", s: headerStyle },
          ],
          dataRows: downtimeReasons.data?.map((reason: DowntimeReasonsGet200ResponseRowsInner) => [
            { v: toString(reason.seq), t: "s", s: dataStyle },
            { v: toString(reason.code), t: "s", s: dataStyle },
            { v: toString(reason.name), t: "s", s: dataStyle },
            { v: toString(reason.operationCode), t: "s", s: dataStyle },
          ]),
        },
        ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING
          ? [
              {
                sheetName: "Grade",
                headerRow: [
                  { v: "Grade코드", t: "s", s: headerStyle },
                  { v: "Grade명", t: "s", s: headerStyle },
                ],
                dataRows: grades.data?.map((grade: GradesGet200ResponseRowsInner) => [
                  { v: toString(grade.code), t: "s", s: dataStyle },
                  { v: toString(grade.name), t: "s", s: dataStyle },
                ]),
              },
            ]
          : []),
        ...(customFunctions.ADD_STANDARD_INFO_DEPART_CODE_MENU_SETTING
          ? [
              {
                sheetName: "부서",
                headerRow: [
                  { v: "부서코드", t: "s", s: headerStyle },
                  { v: "부서명", t: "s", s: headerStyle },
                ],
                dataRows: departments.data?.map(
                  (department: DepartmentsGet200ResponseRowsInner) => [
                    { v: toString(department.code), t: "s", s: dataStyle },
                    { v: toString(department.name), t: "s", s: dataStyle },
                  ],
                ),
              },
            ]
          : []),
        ...(customFunctions.ADD_STANDARD_INFO_PARTNER_MENU_SETTING
          ? [
              {
                sheetName: "협력사",
                headerRow: [
                  { v: "협력사 코드", t: "s", s: headerStyle },
                  { v: "협력사 명", t: "s", s: headerStyle },
                  { v: "협력사 타입", t: "s", s: headerStyle },
                  { v: "연락처", t: "s", s: headerStyle },
                ],
                dataRows: partners.data?.map((partner: PartnersGet200ResponseRowsInner) => [
                  { v: toString(partner.code), t: "s", s: dataStyle },
                  { v: toString(partner.name), t: "s", s: dataStyle },
                  { v: t(toString(partner.type)), t: "s", s: dataStyle },
                  { v: toString(partner.contact), t: "s", s: dataStyle },
                ]),
              },
            ]
          : []),
        ...(customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING
          ? [
              {
                sheetName: "BOM",
                headerRow: [
                  { v: "품목군", t: "s", s: headerStyle },
                  { v: "품목명", t: "s", s: headerStyle },
                  ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING
                    ? [{ v: "Grade명", t: "s", s: headerStyle }]
                    : []),
                  { v: "품목코드", t: "s", s: headerStyle },
                ],
                dataRows: bomSearch.data?.map((item: ItemGradeViewGet200ResponseRowsInner) => [
                  { v: t(toString(item.itemType)), t: "s", s: dataStyle },
                  { v: toString(item.name), t: "s", s: dataStyle },
                  ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING
                    ? [{ v: toString(item.gradeName), t: "s", s: dataStyle }]
                    : []),
                  { v: toString(item.code), t: "s", s: dataStyle },
                ]),
              },
            ]
          : []),
        {
          sheetName: "사용자",
          headerRow: [
            { v: "사용자 코드", t: "s", s: headerStyle },
            { v: "사용자 명", t: "s", s: headerStyle },
            { v: "로그인아이디", t: "s", s: headerStyle },
            { v: "설비코드", t: "s", s: headerStyle },
            { v: "출고 로케이션 명", t: "s", s: headerStyle },
            { v: "입고 로케이션 명", t: "s", s: headerStyle },
            { v: "프린터 코드", t: "s", s: headerStyle },
          ],
          dataRows: users.data?.map((item: AuthSignupPost201Response) => [
            { v: toString(item.code), t: "s", s: dataStyle },
            { v: toString(item.name), t: "s", s: dataStyle },
            { v: toString(item.username), t: "s", s: dataStyle },
            { v: toString(item.equipmentCode), t: "s", s: dataStyle },
            { v: toString(item.fromLocationName), t: "s", s: dataStyle },
            { v: toString(item.toLocationName), t: "s", s: dataStyle },
            { v: toString(item.printerCode), t: "s", s: dataStyle },
          ]),
        },
      ];

      await multiSheetExcelDownload({
        sheets: sheetData,
        fileName: `${today}_기준정보`,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  return { downloadExcel };
};
